import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { verifyEmail as verifyEmailService } from 'Services/VerifyEmail/EmailVerification';
import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import Loading from 'Redesign_Funnel/components/Loading';
import { useLocalStorage } from 'Hooks/useStorage';
import { JSend } from 'Models/Generics/BFF';
import ErrorEmailVerification from './ErrorEmailVerification';
import { ResendEmailVerification } from './ResendEmailVerification';
import SuccessEmailVerification from './SuccessEmailVerification';
import { EMAIL_VERIFICATION_STATUS } from './EmailVerification.constants';
import { EmailVerificationLayout } from './components/EmailVerificationLayout';

const ALREADY_VERIFIED = 'Email already verified';
const Logger = LoggerFactory.getLogger(DOMAIN.CONSUMER_ENABLEMENT);

const EmailVerification = () => {
  const { search: query } = useLocation();
  const sendEmailDefault = query.includes('send_email');
  const token = new URLSearchParams(query).get('token');
  const isSendEmail = !token;
  useLocalStorage(EMAIL_VERIFICATION_STATUS, null);
  const {
    error: errorVerification,
    isError: isErrorVerification,
    isSuccess: isSuccessVerification,
  } = useQuery<unknown, { response: JSend<{ message: string }> }>({
    enabled: !!token,
    queryFn: async () => {
      const response = await verifyEmailService({ token });
      const { status, data } = response || {};

      if (status !== 'success') {
        return Promise.reject(new Error('Verify email failed'));
      }

      if (data.message === ALREADY_VERIFIED) {
        return Promise.reject(new Error(ALREADY_VERIFIED));
      }

      return response?.data;
    },
    queryKey: ['VERIFY_EMAIL', token],
    retry: 0,
  });

  useEffect(() => {
    if (isErrorVerification) {
      Logger.warn(
        'Error Verify Email',
        `${FEATURE.LOGIN}`,
        {
          error: errorVerification?.response?.data?.message ?? errorVerification,
        },
      );
    }
  }, [isErrorVerification]);

  if (isErrorVerification) {
    return (
      <EmailVerificationLayout>
        <ErrorEmailVerification />
      </EmailVerificationLayout>
    );
  }

  if (isSuccessVerification) {
    return (
      <EmailVerificationLayout>
        <SuccessEmailVerification />
      </EmailVerificationLayout>
    );
  }

  if (isSendEmail) {
    return (
      <EmailVerificationLayout>
        <ResendEmailVerification sendByDefault={sendEmailDefault} />
      </EmailVerificationLayout>
    );
  }

  return (
    <EmailVerificationLayout>
      <Loading />
    </EmailVerificationLayout>
  );
};

EmailVerification.propTypes = {};

export default EmailVerification;
